import get from "lodash/get";
import {setPathSchedule, getSortPathInfoList} from "../resources/PathScheduleResource";
import {resolvePairedPaths, SORT_PATH_TYPE} from "./PathScheduleHelper";
import {getNodeId} from "../Utility";
import {
    SORT_PATH_CSV_HEADERS,
    SCHEDULE_STATUS_LABELS,
    DEFAULT_ERROR_MESSAGE,
} from "../constants/SortPathBulkUploadConstants";

export const UNRESOLVED_PATHS_ERROR =
    "Configuration created but Fill/Pick path IDs could not be resolved; not scheduled.";

/**
 * Unwraps an API error message that may be a JSON string carrying a nested `message`
 * (e.g. "{\"message\":\"...\"}"). Returns the inner message when present, otherwise the
 * raw text; DEFAULT_ERROR_MESSAGE when there is no message at all.
 */
export function unwrapErrorMessage(rawMessage) {
    if (!rawMessage) return DEFAULT_ERROR_MESSAGE;
    try {
        const parsed = JSON.parse(rawMessage);
        return get(parsed, ["message"]) || rawMessage;
    } catch (_) {
        return rawMessage;
    }
}

/**
 * Reads the FILL/PICK path IDs from a ConfigureSortPathSettings response:
 * sortPathSetting.sortPaths is a SortPathInfoList ({sortPathId, type}).
 */
export function getPathsFromCreateResponse(configResponse) {
    const sortPaths = get(configResponse, ["sortPathSetting", "sortPaths"]);
    const list = Array.isArray(sortPaths) ? sortPaths : [];
    const idOf = (type) => get(list.find(p => get(p, ["type"]) === type), ["sortPathId"]);
    return {fillPathId: idOf(SORT_PATH_TYPE.FILL), pickPathId: idOf(SORT_PATH_TYPE.PICK)};
}

/**
 * Fallback path resolution via GetSortPathInfoList, matching the created config's
 * settingId + cycle + type. A failed fetch resolves to no paths.
 */
async function getPathsFromSortPathInfoList(settingId, cycle) {
    let sortPathInfoList = [];
    try {
        const infoData = await getSortPathInfoList(getNodeId(), true);
        if (infoData && Array.isArray(infoData.sortPathInfoList)) {
            sortPathInfoList = infoData.sortPathInfoList;
        }
    } catch (_) {
        sortPathInfoList = [];
    }
    return resolvePairedPaths(settingId, cycle, sortPathInfoList);
}

/**
 * Schedules a just-created/updated configuration for a future effective date.
 *
 * FILL/PICK path IDs are read from the create response first; GetSortPathInfoList is
 * only called when they are missing, avoiding a per-row fetch and read-after-write lag.
 * Failure is detected by errorCode (as the Path Schedule tab does), and the error text
 * is unwrapped the same way as configuration errors.
 *
 * @param configResponse ConfigureSortPathSettings output for the row
 * @param row the parsed CSV row
 * @param effectiveDate YYYY-MM-DD schedule start
 * @returns {Promise<{scheduleStatus: string, scheduleError: (string|undefined)}>}
 */
export async function scheduleUploadedConfig(configResponse, row, effectiveDate) {
    const cycle = row[SORT_PATH_CSV_HEADERS.CYCLE];
    let {fillPathId, pickPathId} = getPathsFromCreateResponse(configResponse);
    if (!fillPathId || !pickPathId) {
        const settingId = get(configResponse, ["sortPathSetting", "settingId"]);
        ({fillPathId, pickPathId} = await getPathsFromSortPathInfoList(settingId, cycle));
    }
    if (!fillPathId || !pickPathId) {
        return {scheduleStatus: SCHEDULE_STATUS_LABELS.FAILED, scheduleError: UNRESOLVED_PATHS_ERROR};
    }
    try {
        // New entry: no version (upsert; backend assigns version 1).
        const scheduleData = await setPathSchedule(getNodeId(), cycle, effectiveDate, {fillPathId, pickPathId});
        if (!scheduleData || scheduleData.errorCode) {
            return {
                scheduleStatus: SCHEDULE_STATUS_LABELS.FAILED,
                scheduleError: unwrapErrorMessage(get(scheduleData, ["message"])),
            };
        }
        return {scheduleStatus: SCHEDULE_STATUS_LABELS.SCHEDULED, scheduleError: undefined};
    } catch (error) {
        return {scheduleStatus: SCHEDULE_STATUS_LABELS.FAILED, scheduleError: error.toString()};
    }
}
